import {
	ArrowsClockwiseIcon,
	BalloonIcon,
	BasketIcon,
	ChartBarIcon,
	CubeIcon,
	GearIcon,
	KeyIcon,
	LegoIcon,
	TerminalWindowIcon,
	TriangleIcon,
	UserCircleIcon,
	UsersIcon,
	WebhooksLogoIcon,
} from "@phosphor-icons/react";
import { Scopes } from "@autumn/shared";
import { PanelLeft } from "lucide-react";
import { useHotkeys } from "react-hotkeys-hook";
import { BetaBadge } from "@/components/v2/badges/BetaBadge";
import { Button } from "@/components/v2/buttons/Button";
import { RevenueCatIcon, StripeIcon } from "@/components/v2/icons/AutumnIcons";
import { useAutumnFlags } from "@/hooks/common/useAutumnFlags";
import { useLocalStorage } from "@/hooks/common/useLocalStorage";
import { useScopes } from "@/hooks/useScopes";
import { cn } from "@/lib/utils";
import { useEnv } from "@/utils/envUtils";
import { CollapsibleNavGroup } from "./CollapsibleNavGroup";
import { OrgDropdown } from "./components/OrgDropdown";
import { EnvDropdown } from "./EnvDropdown";
import { NavButton } from "./NavButton";
import SidebarBottom from "./SidebarBottom";
import { SidebarContext } from "./SidebarContext";
import { SidebarRail } from "./SidebarRail";

const buildDevSubTabs = ({
	flags,
}: {
	flags: {
		webhooks: boolean;
		vercel: boolean;
		revenuecat: boolean;
	};
}) => {
	return [
		{
			title: "API Keys",
			value: "api_keys",
			icon: <KeyIcon size={16} weight="fill" />,
		},
		{
			title: "Stripe",
			value: "stripe",
			icon: <StripeIcon size={16} />,
		},
		...(flags.vercel
			? [
					{
						title: "Vercel",
						value: "vercel",
						icon: <TriangleIcon size={16} weight="fill" />,
					},
				]
			: []),
		...(flags.revenuecat
			? [
					{
						title: "RevenueCat",
						value: "revenuecat",
						// icon: <PawPrintIcon size={16} weight="fill" />,
						icon: <RevenueCatIcon size={64} />,
					},
				]
			: []),
		...(flags.webhooks
			? [
					{
						title: "Webhooks",
						value: "webhooks",
						icon: <WebhooksLogoIcon size={16} weight="fill" />,
					},
				]
			: []),
	];
};

export const MainSidebar = ({
	onNavigate,
}: {
	onNavigate?: () => void;
} = {}) => {
	const env = useEnv();

	const flags = useAutumnFlags();
	const { has } = useScopes();
	const canSeeDev = has(Scopes.ApiKeys.Read);
	const canSeeMigrations = has(Scopes.Migrations.Read);

	const [storedExpanded, setExpanded] = useLocalStorage<boolean>(
		"sidebar.expanded",
		true,
	);

	// In mobile sheet mode, always show expanded sidebar
	const isMobileSheet = !!onNavigate;
	const expanded = isMobileSheet ? true : storedExpanded;

	const [productGroupOpen, setProductGroupOpen] = useLocalStorage<boolean>(
		"sidebar.productGroupOpen",
		true,
	);
	const [customerGroupOpen, setCustomerGroupOpen] = useLocalStorage<boolean>(
		"sidebar.customerGroupOpen",
		true,
	);
	const [devGroupOpen, setDevGroupOpen] = useLocalStorage<boolean>(
		"sidebar.devGroupOpen",
		true,
	);

	useHotkeys(["meta+b", "ctrl+b"], () => {
		setExpanded((prev) => !prev);
	});

	const onProductTabClick = () => {
		setProductGroupOpen((prev) => !prev);
	};

	return (
		<SidebarContext.Provider value={{ expanded, setExpanded, onNavigate }}>
			<div
				className={cn(
					`h-full py-4 flex flex-col justify-between transition-all duration-150 relative`,
					isMobileSheet
						? "min-w-[200px]"
						: expanded
							? "min-w-[200px] max-w-[200px]"
							: "min-w-[50px] max-w-[50px]",
				)}
			>
				<div className="flex flex-col gap-6 relative">
					{!isMobileSheet && (
						<Button
							variant="secondary"
							size="sm"
							onClick={() => {
								setExpanded((prev) => !prev);
							}}
							className={cn(
								"absolute top-1 right-4 text-tertiary-foreground hover:bg-stone-200 w-5 h-5 p-0 border-none border-0 shadow-none !bg-transparent",
								expanded
									? "opacity-100 transition-opacity duration-100"
									: "opacity-0 transition-opacity duration-100",
							)}
						>
							<PanelLeft size={14} />
						</Button>
					)}
					<OrgDropdown />
					<EnvDropdown env={env} />
					<div className="flex flex-col px-2 gap-1">
						<CollapsibleNavGroup
							value="products"
							icon={<BasketIcon size={16} weight="fill" />}
							title="Products"
							env={env}
							isOpen={productGroupOpen}
							onToggle={onProductTabClick}
							subTabs={[
								{
									title: "Plans",
									value: "products",
									icon: <CubeIcon size={16} weight="fill" />,
								},
								{
									title: "Features",
									value: "features",
									icon: <LegoIcon size={16} weight="fill" />,
								},
								{
									title: "Rewards",
									value: "rewards",
									icon: <BalloonIcon size={16} weight="fill" />,
								},
							]}
						/>
						{canSeeMigrations ? (
							<CollapsibleNavGroup
								value="customers"
								icon={<UserCircleIcon size={16} weight="fill" />}
								title="Customers"
								env={env}
								isOpen={customerGroupOpen}
								onToggle={() => setCustomerGroupOpen((prev) => !prev)}
								subTabs={[
									{
										title: "All Customers",
										value: "customers",
										icon: <UsersIcon size={16} weight="fill" />,
									},
									{
										title: "Migrations",
										value: "migrations",
										path: "/migrations",
										icon: <ArrowsClockwiseIcon size={16} weight="fill" />,
										badge: <BetaBadge className="ml-auto" />,
									},
								]}
							/>
						) : (
							<NavButton
								value="customers"
								icon={<UserCircleIcon size={16} weight="fill" />}
								title="Customers"
								env={env}
							/>
						)}
						<NavButton
							value="analytics"
							icon={<ChartBarIcon size={16} weight="fill" />}
							title="Analytics"
							env={env}
						/>
						{canSeeDev && (
							<CollapsibleNavGroup
								value="dev"
								icon={<TerminalWindowIcon size={16} weight="fill" />}
								title="Developer"
								env={env}
								isOpen={devGroupOpen}
								onToggle={() => setDevGroupOpen((prev) => !prev)}
								subTabs={buildDevSubTabs({ flags })}
							/>
						)}
						<NavButton
							value="settings"
							icon={<GearIcon size={16} weight="fill" />}
							title="Settings"
							env={env}
						/>
					</div>
				</div>

				<SidebarBottom />
				{!isMobileSheet && <SidebarRail />}
			</div>
		</SidebarContext.Provider>
	);
};
